import {
  FunctionComponent,
  PropsWithChildren,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { SearchInput, Sidebar, SidebarAdmin } from '../../../components';
import { ConfigContext, UserContext } from '../../../context';
import { useFreeLoginPopup, useIsNarrowViewport, useLeaderBoard } from '../../hooks';
import { getCssVar, isAdminLayout, layoutPath } from '../../utils';
import { Avatar } from '../Avatar';
import { Button, ButtonType } from '../Button';
import { ConfigurationLink } from '../ConfigurationLink';
import { IconLabel } from '../IconLabel';
import { ModalSlidePanel, PanelSide } from '../ModalSlidePanel';

import classNames from 'classnames';
import classes from './NavbarCommon.module.scss';

export const NavbarCommon: FunctionComponent<PropsWithChildren> = memo(({ children }) => {
  const { t } = useTranslation();

  const isNarrowViewport = useIsNarrowViewport();

  const { pathname, search } = useLocation();

  const { freeLoginModal, freeLoginHandler } = useFreeLoginPopup();

  const { isLeaderBoard } = useLeaderBoard();

  const { isAnonymous, avatar } = useContext(UserContext).userProfile;

  const {
    organisationLogoUrl,
    headerLinks,
    meta: { title },
    mobile: { menuIconColor },
  } = useContext(ConfigContext).config.elements;

  const renderHeaderLinks = useMemo(
    () => headerLinks.map((link) => <ConfigurationLink key={link.title} link={link} />),
    [headerLinks]
  );

  const [mobilePanelOpen, setMobilePanelOpen] = useState(false);

  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const mobilePanelClose = useCallback(() => {
    setMobilePanelOpen(false);
  }, []);

  useEffect(() => {
    setIsSearchOpen(false);
  }, [pathname, search, isNarrowViewport]);

  const renderAvatar = useMemo(() => {
    if (isAnonymous) {
      return (
        <Button type={ButtonType.primary} label={t('common.join')} onClick={freeLoginHandler} />
      );
    }

    return (
      <Link
        className={classes['navbar__avatar']}
        onClick={() => window.scrollTo(0, 0)}
        to={layoutPath('/profile')}
        aria-label={t('profile.open-profile')}
      >
        <Avatar url={avatar?.url} />
      </Link>
    );
  }, [avatar?.url, freeLoginHandler, isAnonymous, t]);

  const mobilePanelComponent = useMemo(() => {
    if (isAdminLayout()) {
      return <SidebarAdmin onMobilePanelClose={mobilePanelClose} />;
    }

    return <Sidebar onMobilePanelClose={mobilePanelClose} />;
  }, [mobilePanelClose]);

  const renderHeaderContent = useMemo(() => {
    if (isNarrowViewport && isSearchOpen) {
      return (
        <>
          <IconLabel
            iconId={'arrow-left'}
            ariaLabel={t('common.back')}
            color={getCssVar('--settings-icon-color')}
            hoverColor={getCssVar('--settings-icon-hover-color')}
            onClick={() => setIsSearchOpen(false)}
          />
          <div className={classNames(classes['navbar__search'], classes['navbar__search--narrow'])}>
            <SearchInput autoFocus />
          </div>
        </>
      );
    }

    return (
      <>
        {isNarrowViewport && (
          <>
            <IconLabel
              iconId={'mobile-menu'}
              ariaLabel={t('common.menu')}
              onClick={() => setMobilePanelOpen(!mobilePanelOpen)}
              color={menuIconColor}
              singleColor
            />
            <ModalSlidePanel
              isOpen={mobilePanelOpen}
              body={mobilePanelComponent}
              side={PanelSide.left}
              onClose={mobilePanelClose}
              ariaLabel={t('common.menu')}
            />
          </>
        )}

        <div className={classes['navbar__logo-wrapper']}>
          <Link className={classes['navbar__logo']} to={layoutPath('/')}>
            <img
              src={organisationLogoUrl}
              className={classNames(classes['navbar__logo-icon'], {
                [classes['navbar__logo-icon--narrow']]: isNarrowViewport,
              })}
              alt={title}
            />
          </Link>
        </div>

        {!isNarrowViewport && !isLeaderBoard && (
          <div className={classes['navbar__search']}>
            <SearchInput />
          </div>
        )}

        <div className={classes['navbar__links']}>
          {isNarrowViewport && !isLeaderBoard && (
            <IconLabel
              iconId={'search'}
              ariaLabel={t('common.search')}
              color={getCssVar('--settings-icon-color')}
              hoverColor={getCssVar('--settings-icon-hover-color')}
              onClick={() => setIsSearchOpen(true)}
            />
          )}

          {!isNarrowViewport && !isLeaderBoard && renderHeaderLinks}

          {!isLeaderBoard && children}

          {renderAvatar}
        </div>
      </>
    );
  }, [
    children,
    isLeaderBoard,
    isNarrowViewport,
    isSearchOpen,
    menuIconColor,
    mobilePanelClose,
    mobilePanelComponent,
    mobilePanelOpen,
    organisationLogoUrl,
    renderAvatar,
    renderHeaderLinks,
    t,
    title,
  ]);

  return (
    <>
      <header className={classes['navbar']}>
        <div
          className={classNames(classes['navbar__container'], {
            [classes['navbar__container--narrow']]: isNarrowViewport,
          })}
        >
          {renderHeaderContent}
        </div>
      </header>

      {freeLoginModal}
    </>
  );
});
